import { DayDepthChartWidgetState } from '@go-widgets/well-logs-widget';
import type { DayDepthChartInitParams } from '@go-widgets/well-logs-widget';
import { action, comparer, makeObservable, observable, reaction } from 'mobx';
import type { IReactionDisposer, ObservableMap } from 'mobx';

import { GO_REPORT_API_URL, SHOW_GO_REPORT_CHARTS } from 'src/api/consts';
import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import type { WidgetEntity } from 'src/entities/widget/WidgetEntity';
import { requireService } from 'src/packages/di';
import { getNewAndDeletedFields } from 'src/packages/shared/utils/getNewAndDeletedElements';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import { WellListWidgetEntity } from '../well-list-widget/WellListWidget.entity';

import type { DayDepthChartWidgetEntity } from './DayDepthChartWidget.entity';
import { getCorrectLocale } from './utils';

export class DayDepthChartWidgetStore implements IWidgetStore {
  private readonly dayDepthChartEntity: DayDepthChartWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: DayDepthChartInitParams;

  @observable isLoading: boolean = false;
  @observable isReady: boolean = false;
  @observable hasError: boolean = false;
  @observable disposers: ObservableMap<WidgetEntity, IReactionDisposer> = observable.map();
  @observable isGroupSelectOpen: boolean = false;

  constructor(
    dayDepthChartEntity: DayDepthChartWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly navigate: (wellId: number | null, wellboreId?: number | null) => void,
    private readonly wellListPanel: TWellListCombinedData,
    private readonly theme = requireService('theme'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly language = requireService('language'),
    private readonly userPreferencesService = requireService('userPreference'),
  ) {
    this.dayDepthChartEntity = dayDepthChartEntity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = dayDepthChartEntity.isGroupsDefaultOpened;

    this.initParams = {
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
      axiosInstance: wellLogsWidgetAgent,
      showDataFromGoReport: SHOW_GO_REPORT_CHARTS,
      goReportServiceUrl: GO_REPORT_API_URL,
      backendSimplifyMethodEpsilon: 2,
    };

    makeObservable(this);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  exitFullScreen() {
    this.dayDepthChartEntity.setFullscreen(false);
  }

  @action.bound
  setGroupForWellLogs(group: TGroup | null): void {
    this.dayDepthChartEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  setWellId(well: number | null): void {
    this.dayDepthChartEntity.setWellId(well);
  }

  private mapNewWidgets(widgets: WidgetEntity[]): void {
    for (const widget of widgets) {
      if (!(widget instanceof WellListWidgetEntity) || this.disposers.has(widget)) {
        continue;
      }

      const disposeWellListWidget = reaction(
        () => widget.selectedWellID,
        (wellId) => {
          this.dayDepthChartEntity.setWellId(wellId);
        },
      );

      this.disposers.set(widget, disposeWellListWidget);
    }
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  private mapDeletedWidgets(widgets: WidgetEntity[]): void {
    for (const widget of widgets) {
      if (this.disposers.has(widget)) {
        const disposer = this.disposers.get(widget);

        disposer?.();

        this.disposers.delete(widget);
      }
    }
  }

  @action.bound
  redirectToBasicParameters() {
    const wellId = this.dayDepthChartEntity.wellId;
    const queryParams = new URLSearchParams();

    if (wellId) {
      queryParams.set('wellId', wellId.toString());
    }

    this.navigate(wellId);
  }

  private getNewState(wellId: number): DayDepthChartWidgetState {
    return new DayDepthChartWidgetState(
      this.initParams,
      {
        // TODO: remove hardcode
        wellId,
        width: 500,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        fullScreen: this.dayDepthChartEntity.isFullscreen,
      },
      this.dayDepthChartEntity.stateParams,
      {
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInternalStateChanged: (state) => {
          this.dayDepthChartEntity.setStateParams(state);
        },
        onTitleChanged: (value) => {
          this.dayDepthChartEntity.setCenterTitle(value);
        },
        onWellboreChanged: (wellboreName) => {
          this.dayDepthChartEntity.setWellboreName(wellboreName);
        },
        onRedirectToBasicParameters: () => {
          this.redirectToBasicParameters();
        },
      },
    );
  }

  effect = () => {
    const disposeGroupId = reaction(
      () => this.dayDepthChartEntity.groupId,
      (groupId) => {
        if (groupId === null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.dayDepthChartEntity, this.wellListPanel);
      },
    );

    const dispose = reaction(
      () => this.groupSelectStore.groupManager.getWidgetsByGroupId(this.dayDepthChartEntity.groupId).slice(),
      (widgets, prevState) => {
        const prevWidgets = prevState ?? [];

        const { newElements: newWidgets, deletedElements: deletedWidgets } = getNewAndDeletedFields<WidgetEntity>(
          widgets,
          prevWidgets,
        );

        this.mapNewWidgets(newWidgets);
        this.mapDeletedWidgets(deletedWidgets);
      },
      { fireImmediately: true, equals: comparer.shallow },
    );

    const disposeWellId = reaction(
      () => this.dayDepthChartEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.dayDepthChartEntity.state) {
          const newState = this.getNewState(wellId);

          this.dayDepthChartEntity.setState(newState);
        } else if (wellId && this.dayDepthChartEntity.state && typeof prevWellId !== 'undefined') {
          this.dayDepthChartEntity.state.setAppParams({ wellId });
        } else if (!wellId && this.dayDepthChartEntity.state) {
          this.dayDepthChartEntity.state.destroy();
          this.dayDepthChartEntity.setState(null);
        }
      },
      { fireImmediately: true },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      }),
      ({ theme, language, isAltNames }) => {
        this.dayDepthChartEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
        });
      },
      {
        fireImmediately: true,
      },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.dayDepthChartEntity.groupId) {
          this.dayDepthChartEntity.setWellId(wellId);
        }
      },
      {
        fireImmediately: true,
      },
    );

    return () => {
      dispose();
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
    };
  };
}
